import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFoundingSeats } from '../hooks/useFoundingSeats';
import './HeroSection.css';

/** Hero section id — matches the `sections` entry so the ticker, scroll
 * navigation and video parallax keep tracking it. */
export const HERO_ID = 'hero';

interface HeroSectionProps {
  /** Drives the entrance reveal — set when this is the active scroll section. */
  isActive: boolean;
}

/**
 * Founding-cohort home hero. Replaces the generic section render for `hero`
 * so it can show the live seat count and a claimed-seats progress bar.
 */
export function HeroSection({ isActive }: HeroSectionProps) {
  const { seatsRemaining, seatsTotal, soldOut, status, hasLiveCount } = useFoundingSeats();
  const remaining = seatsRemaining ?? seatsTotal;
  const claimed = seatsTotal - remaining;
  const claimedPct = seatsTotal > 0 ? Math.round((claimed / seatsTotal) * 100) : 0;

  // Neutral copy until a live count loads, so we never advertise a specific
  // number of open seats we can't confirm.
  const seatLabel = soldOut ? 'full' : hasLiveCount ? `${remaining} seats` : 'limited seats';
  const claimLabel =
    status === 'unavailable'
      ? 'Get early access'
      : soldOut
        ? 'Join the waitlist'
        : 'Claim your 6 months free';

  // Set the fill width imperatively (matches the codebase's ref-driven styling
  // in VideoBackground and avoids inline style props). Runs once the progress
  // bar mounts (i.e. a live count is available).
  const fillRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (fillRef.current) fillRef.current.style.width = `${claimedPct}%`;
  }, [claimedPct, hasLiveCount]);

  return (
    <section
      id={HERO_ID}
      className="section section--hero hero"
      aria-label="Revvly founding cohort"
    >
      <div className={`hero__inner ${isActive ? 'hero__inner--visible' : ''}`}>
        <p className="hero__eyebrow">
          <span className="hero__eyebrow-rule" aria-hidden />
          Founding cohort · {seatLabel}
        </p>

        <h2 className="section__headline section__headline--hero hero__headline">
          Orchestrate the
          <br />
          flow of <em className="section__accent">revenue</em>
          <span className="section__accent">.</span>
        </h2>

        <p className="hero__subtitle">
          Subscriptions, pricing, payments, invoicing, FX, forecasting, and intelligence in one
          engine. The first <strong>{seatsTotal} teams</strong> to join get{' '}
          <strong>6 full months free</strong>, in exchange for a 15-minute monthly call with our
          CEO.
        </p>

        {/* Preserve the SEO keywords dropped from the visible hero copy. */}
        <p className="sr-only">
          Subscription revenue management for SaaS: MRR tracking, dunning, churn analytics, and
          pricing in one platform.
        </p>

        <div className="hero__ctas">
          <Link to="/early-access" className="section__cta section__cta--accent">
            {claimLabel}
            <span className="section__cta-icon">›</span>
          </Link>
          <Link to="/pricing" className="section__cta section__cta--secondary">
            See pricing
          </Link>
        </div>

        {hasLiveCount && (
          <div className="hero__progress" role="group" aria-label="Founding seats claimed">
            <div className="hero__progress-head">
              <span className="hero__progress-label">Founding seats claimed</span>
              <span className="hero__progress-count">
                {claimed} / {seatsTotal}
              </span>
            </div>
            <div className="hero__progress-track">
              <span ref={fillRef} className="hero__progress-fill" />
            </div>
          </div>
        )}

        <p className="hero__note">No credit card. Use AI to automate your revenue operations.</p>
      </div>
    </section>
  );
}
